import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./AccessoryConfigurations.css";

function AccessoryConfigurations() {
    const { accessoryId } = useParams();
    const navigate = useNavigate();
    const [accessory, setAccessory] = useState(null);
    const [parameters, setParameters] = useState([]);
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [configToDelete, setConfigToDelete] = useState(null);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [isLocked, setIsLocked] = useState(false);

    useEffect(() => {
        fetchData();
    }, [accessoryId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [accRes, cfgRes] = await Promise.all([
                axios.get(`/accessories/${accessoryId}`),
                axios.get(`/accessories/${accessoryId}/configurations`),
            ]);
            setAccessory(accRes.data.data);
            setIsLocked(accRes.data.data?.is_locked || false);
            setParameters(cfgRes.data.data.parameters || []);
            setConfigurations(cfgRes.data.data.configurations || []);
        } catch (error) {
            setMessage({
                type: "error",
                text: error.response?.data?.message || "Failed to load data",
            });
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(
                `/accessories/${accessoryId}/configurations/${configToDelete.id}`
            );
            showMessage("success", "Configuration deleted successfully");
            setConfigToDelete(null);
            fetchData();
        } catch (error) {
            showMessage("error", error.response?.data?.message || "Failed to delete");
        }
    };

    // Show first 5 parameters as table columns
    const displayParams = parameters.slice(0, 5);

    return (
        <div className="acc-configs-container">
            <div className="acc-configs-header">
                <div>
                    <button
                        className="btn-back"
                        onClick={() => navigate("/admin/accessory-component")}
                    >
                        ← Back to Component Types
                    </button>
                    <nav className="breadcrumb">
                        Accessory Component Master &gt; {accessory?.name}
                    </nav>
                    <h1 className="acc-configs-title">
                        {accessory?.name} — Configurations
                    </h1>
                    <p className="acc-configs-subtitle">
                        Manage model configurations for this component type
                    </p>
                </div>
                <button
                    className="btn-add-config"
                    onClick={() =>
                        navigate(`/admin/accessory-component/${accessoryId}/configurations/new`)
                    }
                    disabled={isLocked}
                    title={isLocked ? "Accessory is locked. Cannot add configurations." : "Add new configuration"}
                    style={{ opacity: isLocked ? 0.5 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                >
                    + Add New Configuration
                </button>
                {isLocked && <div className="lock-warning">🔒 This accessory is locked</div>}
            </div>

            {message.text && (
                <div className={`alert-message alert-${message.type}`}>{message.text}</div>
            )}

            {parameters.length === 0 && !loading && (
                <div className="info-banner">
                    ℹ️ No parameters defined yet for <strong>{accessory?.name}</strong>. Go to{" "}
                    <button
                        className="link-btn"
                        onClick={() =>
                            navigate(`/admin/accessories-config/accessories/${accessoryId}/parameters`)
                        }
                    >
                        Accessories Configuration Master
                    </button>{" "}
                    to define parameters first.
                </div>
            )}

            {loading ? (
                <div className="loading-container">Loading configurations...</div>
            ) : (
                <div className="table-card">
                    <div className="table-responsive">
                        <table className="configs-table">
                            <thead>
                                <tr>
                                    <th>Selector (Model)</th>
                                    {displayParams.map((p) => (
                                        <th key={p.id}>{p.name}</th>
                                    ))}
                                    {parameters.length > 5 && (
                                        <th>+{parameters.length - 5} more</th>
                                    )}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {configurations.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={displayParams.length + 2}
                                            className="no-data-row"
                                        >
                                            No configurations yet.{" "}
                                            <button
                                                className="link-btn"
                                                onClick={() =>
                                                    navigate(
                                                        `/admin/accessory-component/${accessoryId}/configurations/new`
                                                    )
                                                }
                                            >
                                                Add New Configuration →
                                            </button>
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {configurations.map((config) => (
                                            <tr key={config.id}>
                                                <td className="model-cell">{config.model}</td>
                                                {displayParams.map((p) => (
                                                    <td key={p.id} className="value-cell">
                                                        {config.values?.[p.id] || (
                                                            <span className="dash-cell">—</span>
                                                        )}
                                                    </td>
                                                ))}
                                                {parameters.length > 5 && <td className="dash-cell">…</td>}
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-edit"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/admin/accessory-component/${accessoryId}/configurations/${config.id}/edit`
                                                                )
                                                            }
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="btn-delete"
                                                            onClick={() => setConfigToDelete(config)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Add New Configuration row */}
                                        {!isLocked && (
                                            <tr className="add-new-row">
                                                <td
                                                    colSpan={displayParams.length + 2}
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/accessory-component/${accessoryId}/configurations/new`
                                                        )
                                                    }
                                                >
                                                    <span className="add-new-label">
                                                        + Add New Configuration →
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {configToDelete && (
                <div className="modal-overlay" onClick={() => setConfigToDelete(null)}>
                    <div
                        className="confirm-dialog"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="confirm-title">Confirm Deletion</h2>
                        <p className="confirm-text">
                            Are you sure you want to delete the configuration for model "
                            <strong>{configToDelete.model}</strong>"?
                        </p>
                        <div className="confirm-warning">
                            This will permanently delete all parameter values for this model.
                        </div>
                        <div className="confirm-actions">
                            <button className="btn-cancel" onClick={() => setConfigToDelete(null)}>
                                Cancel
                            </button>
                            <button className="btn-confirm-delete" onClick={handleDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AccessoryConfigurations;
